/**
 * Analysis Run API Service
 * Handles all API calls related to analysis runs
 */

import { BaseAPIService } from './client';
import { AnalysisRun, AnalysisRunCreate, AnalysisRunUpdate } from '../../types/analysisRun';

class AnalysisRunsAPI extends BaseAPIService {
  /**
   * Create a new analysis run
   */
  async createAnalysisRun(data: AnalysisRunCreate): Promise<AnalysisRun> {
    const response = await this.post<AnalysisRun>('/api/v1/analysis-runs', data);
    return response;
  }

  /**
   * Get all analysis runs for the current user
   */
  async getAnalysisRuns(): Promise<AnalysisRun[]> {
    const response = await this.get<AnalysisRun[]>('/api/v1/analysis-runs');
    return Array.isArray(response) ? response : [];
  }

  /**
   * Get a specific analysis run by ID
   */
  async getAnalysisRun(analysisRunId: string): Promise<AnalysisRun> {
    const response = await this.get<AnalysisRun>(`/api/v1/analysis-runs/${analysisRunId}`);
    return response;
  }

  /**
   * Update an analysis run
   */
  async updateAnalysisRun(analysisRunId: string, data: AnalysisRunUpdate): Promise<AnalysisRun> {
    const response = await this.patch<AnalysisRun>(`/api/v1/analysis-runs/${analysisRunId}`, data);
    return response;
  }

  /**
   * Delete an analysis run
   */
  async deleteAnalysisRun(analysisRunId: string): Promise<void> {
    await this.delete(`/api/v1/analysis-runs/${analysisRunId}`);
  }

  /**
   * Get analysis runs for a specific matter
   */
  async getMatterAnalysisRuns(matterId: string): Promise<AnalysisRun[]> {
    const allRuns = await this.getAnalysisRuns();
    return allRuns.filter(run => run.matter_id === matterId);
  }
}

export const analysisRunsApi = new AnalysisRunsAPI();
